// TAS-181: session recovery safeguards for the connection-reliability epic.
//
// Two escape hatches for when the automatic reconcile can't be trusted:
//
//   1. readLiveStateFresh() — a FRESHNESS-GUARDED read of session_live_state.
//      Supabase GETs can geo-route to an async read replica, so a read can come
//      back STALER than state we've already applied. When it does, we're likely
//      hitting a lagging path — retry with backoff before trusting it, instead of
//      silently accepting a stale row. (The reconcile's decideLiveStateApply()
//      already ignores a stale row; this makes the read itself actively re-try so
//      the *reload* path below returns the truth rather than the replica's echo.)
//
//      Part 2 (H4/H5): the read also carries `payload` (round_id + attempt, so a
//      question restart reaches a phone that only reads) and `gm_player_id` (the
//      GM-change signal; who the GM IS still comes from session_users_v2).
//
//   2. recoverSession() — the manual stuck-CTA. It re-AUTHENTICATES first (a stale
//      JWT silently fails RLS-guarded reads / auth-gated RPCs) and THEN triggers a
//      fresh reconcile — deliberately NOT "repeat the same failed SELECT". If no
//      reconciler is registered (we're outside a live game), a full reload re-runs
//      the mount catch-up.
//
// The reconciler registry decouples the App-level ConnectivityBanner (which owns
// the CTA) from GameFlowProvider (which owns the authoritative re-read) — the
// banner renders ABOVE the provider in the tree, so a direct ref call isn't
// possible.

import { supabase } from '@tasted/utils/supabase';
import { track } from '@tasted/utils/analytics';

// Backoff for the freshness retry: short, since this races live gameplay.
const FRESH_RETRY_ATTEMPTS = 3;
const FRESH_RETRY_BASE_MS = 150;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Reconcilers registered by the session supervisor (GameFlowProvider). Each is an
// async fn that re-reads authoritative state and applies it forward.
const reconcilers = new Set();

/**
 * Register an authoritative re-read fn so recoverSession() can trigger it.
 * @param {() => (void | Promise<void>)} fn
 * @returns {() => void} unregister
 */
export function registerReconciler(fn) {
  if (typeof fn !== 'function') return () => {};
  reconcilers.add(fn);
  return () => reconcilers.delete(fn);
}

/**
 * True when a value is a newer live-state row than `prev` (run change wins,
 * else higher version). Used to keep the freshest of several reads.
 */
function isFresher(row, prev) {
  if (!prev) return true;
  if (row.run_id && prev.run_id && row.run_id !== prev.run_id) return true; // different run — take latest read
  return typeof row.version === 'number' && typeof prev.version === 'number'
    ? row.version >= prev.version
    : true;
}

/**
 * Read session_live_state with a freshness guard. If the row is STALER than what
 * we've already seen (same run, version < minVersion) we retry with backoff, on
 * the theory that we hit a lagging replica; we return the freshest row observed.
 *
 * @param {string} sessionCode
 * @param {object} [opts]
 * @param {number} [opts.minVersion]  our last-seen version (retry if the read is below it)
 * @param {string|null} [opts.runId]  our last-seen run_id (a run change is never "stale")
 * @param {number} [opts.attempts]
 * @returns {Promise<object|null>} the freshest row, or null if there is none
 */
export async function readLiveStateFresh(
  sessionCode,
  { minVersion = -1, runId = null, attempts = FRESH_RETRY_ATTEMPTS } = {}
) {
  if (!sessionCode) return null;
  let best = null;
  let sawStale = false;
  for (let i = 0; i < attempts; i++) {
    let data = null;
    let error = null;
    try {
      ({ data, error } = await supabase
        .from('session_live_state')
        .select('run_id, version, step_index, phase, payload, gm_player_id')
        .eq('session_id', sessionCode)
        .maybeSingle());
    } catch (err) {
      error = err;
    }

    if (error) {
      if (i < attempts - 1) await delay(FRESH_RETRY_BASE_MS * (i + 1));
      continue;
    }
    if (!data) return best; // no row exists → nothing to be stale about

    if (isFresher(data, best)) best = data;

    // Fresh enough if it's a different run (an epoch reset is authoritative) or
    // its version has caught up to what we've already applied.
    const sameRun = !runId || data.run_id === runId;
    const stale = sameRun && typeof data.version === 'number' && data.version < minVersion;
    if (!stale) return best;

    sawStale = true;
    if (i < attempts - 1) await delay(FRESH_RETRY_BASE_MS * (i + 1));
  }
  if (sawStale) {
    try { track('Stale Read Retried', { sessionCode, minVersion }); } catch { /* analytics never breaks recovery */ }
  }
  return best;
}

/**
 * Manual recovery for a wedged connection / expired token / RLS failure. Refreshes
 * the auth session FIRST (so a stale JWT can't keep failing reads), then triggers
 * every registered reconciler. Falls back to a full reload when none is registered.
 *
 * @returns {Promise<void>}
 */
export async function recoverSession() {
  try { track('Manual Reload Clicked'); } catch { /* analytics never breaks recovery */ }

  // Re-authenticate. Anon players have no Supabase session, so this rejects — that
  // is expected and harmless; authenticated hosts/GMs get a fresh JWT.
  try {
    await supabase.auth?.refreshSession?.();
  } catch { /* no session to refresh (anon) — continue to the re-read */ }

  if (reconcilers.size === 0) {
    if (typeof window !== 'undefined') window.location.reload();
    return;
  }

  await Promise.all(
    [...reconcilers].map(async (fn) => {
      try { await fn(); } catch { /* best-effort; other reconcilers still run */ }
    })
  );
}

// Test-only: clear the registry between cases.
export function __resetSessionRecovery() {
  reconcilers.clear();
}
